"use client";

import { useCallback, useEffect, useState, useSyncExternalStore } from "react";
import type { Session, User } from "./client";
import { type HasuraClaims, nhost } from "./client";

let cachedSession: Session | null = null;

function subscribeToSession(callback: () => void): () => void {
  if (typeof window !== "undefined") {
    cachedSession = nhost.getUserSession();
  }
  return nhost.sessionStorage.onChange((newSession) => {
    cachedSession = newSession;
    callback();
  });
}

function getSessionSnapshot(): Session | null {
  if (cachedSession === null && typeof window !== "undefined") {
    cachedSession = nhost.getUserSession();
  }
  return cachedSession;
}

function getServerSnapshot(): Session | null {
  return null;
}

export function useNhostSession(): Session | null {
  return useSyncExternalStore(subscribeToSession, getSessionSnapshot, getServerSnapshot);
}

export function useAccessToken(): string | null {
  return useNhostSession()?.accessToken ?? null;
}

export function useUserData(): User | null {
  return useNhostSession()?.user ?? null;
}

export function useUserId(): string | null {
  return useNhostSession()?.user?.id ?? null;
}

export function useDecodedAccessToken() {
  return useNhostSession()?.decodedToken ?? null;
}

export function useHasuraClaims(): HasuraClaims | null {
  const decodedToken = useDecodedAccessToken();
  return (decodedToken?.["https://hasura.io/jwt/claims"] as HasuraClaims) ?? null;
}

export function useAuthenticationStatus(): {
  isAuthenticated: boolean;
  isLoading: boolean;
} {
  const [isLoading, setIsLoading] = useState(true);
  const session = useNhostSession();

  useEffect(() => {
    const checkAuth = async () => {
      const currentSession = nhost.getUserSession();
      if (!currentSession) {
        setIsLoading(false);
        return;
      }
      try {
        await nhost.refreshSession(60);
      } catch (error) {
        console.error("Failed to refresh session:", error);
      }
      setIsLoading(false);
    };
    checkAuth();
  }, []);

  return {
    isAuthenticated: !!session?.accessToken,
    isLoading,
  };
}

export function useSignOut() {
  const [isLoading, setIsLoading] = useState(false);

  const signOut = useCallback(async (options?: { all?: boolean }) => {
    setIsLoading(true);
    try {
      const session = nhost.getUserSession();
      await nhost.auth.signOut({ refreshToken: session?.refreshToken, all: options?.all });
    } catch (error) {
      console.error("Sign-out error:", error);
    } finally {
      nhost.clearSession();
      setIsLoading(false);
    }
  }, []);

  return { signOut, isLoading };
}
